import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { WatchlistUtils } from '../../../../../services/watchlist/watchlist-utils';
import { DetailedQuote, SymbolType } from '../../../../../domain/public-api';
import { SecuritySymbol } from '../../../../../domain/symbol.domain';
import {
  TickerSymbolWarningsComponent
} from '../../../../ticker-symbol/ticker-symbol-warning/ticker-symbol-warnings.component';
import { TickerSymbolStatus } from '../../../../ticker-symbol/ticker-symbol.domain';
import { AddToWatchlistComponent } from '../../add-to-watchlist/add-to-watchlist.component';
import { SlideAddToWatchlistComponent } from '../../add-to-watchlist/slide-add-to-watchlist.component';
import { AddToWatchlistToastMessageDirective } from '../../add-to-watchlist/add-to-watchlist-toast-message.directive';
import { InterlistedExchange, QuoteIssueType } from '../../domain';
import { TodaysChangeComponent } from '../../todays-change/todays-change.component';
import { SplitButtonsComponent } from '../../../../../components/split-buttons/split-buttons.component';
import { SplitButtonComponent } from '../../../../../components/split-buttons/split-button.component';
import { SplitButtonDataComponent } from '../../../../../components/split-buttons/split-button-data.component';
import { PlaceholderPipe } from '../../../../../pipes/placeholder.pipe';
import { IconFlagComponent } from '../../../../../components/icon/icon-flag.component';
import { CurrencyPipe } from '../../../../../pipes/currency.pipe';
import { SecurityService } from '../../../../../services/security.service';
import { TranslatePipe } from '../../../../../pipes/translate.pipe';

export type SymbolWithExchanges =
  Pick<DetailedQuote, 'market' | 'symbol' | 'name' | 'issueType' | 'interlistedExchanges' | 'lastPrice' | 'securityHalted' | 'isHalted'>;

@Component({
  selector: 'td-wb-detailed-symbol-header',
  templateUrl: 'detailed-symbol-header.component.html',
  styleUrls: ['detailed-symbol-header.component.scss'],
  imports: [
    NgClass,
    SplitButtonsComponent,
    SplitButtonComponent,
    SplitButtonDataComponent,
    FormsModule,
    PlaceholderPipe,
    IconFlagComponent,
    TranslatePipe,
    TranslateDirective,
    TickerSymbolWarningsComponent,
    CurrencyPipe,
    TodaysChangeComponent,
    AddToWatchlistComponent,
    SlideAddToWatchlistComponent,
    AddToWatchlistToastMessageDirective,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DetailedSymbolHeaderComponent implements OnChanges  {
  @Input() viewSizeCompact: boolean = false;
  @Input() quote: SymbolWithExchanges;
  @Input() displaySymbolDetail?: boolean;
  @Input() symbol: SecuritySymbol;
  @Input() hideAddToWatchlistButton: boolean;
  @Input() hideInterlistExchangeForOTC?: boolean = false;
  @Input() useSlider: boolean = false; // REFACTOR. FOR ET ONLY
  @Output() onShowExchange: EventEmitter<void> = new EventEmitter<void>();

  selectedMarket: string;
  markets: Array<string> = [];

  constructor(private securityService: SecurityService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.quote !== undefined &&
        changes.quote.currentValue !== undefined  &&
        this.hasInterlistedExchanges()) {
      this.selectedMarket = this.quote.market;
      if (this.markets.length !== 2) {
        this.markets[0] = this.quote.market;
        this.markets[1] = this.quote.interlistedExchanges[0].exchange;
      }
    }
  }

  /**
   * Return true if quote has inter listed exchanges
   *
   * @return
   */
  hasInterlistedExchanges(): boolean {
    return this.quote?.interlistedExchanges !== undefined &&
           this.quote?.interlistedExchanges?.length > 0;
  }

  /**
   * Return true if quote issue type is 'STOCK' or 'ETF'
   *
   * @return
   */
  isStockEtf(): boolean {
    return this.quote?.issueType === QuoteIssueType.STOCK || this.quote?.issueType === QuoteIssueType.ETF;
  }

  isETF(): boolean {
    return this.symbol.subTypeCd === SymbolType.ETF;
  }

  /**
   * Return true if quote issue type is 'INDEX'
   *
   * @return
   */
  isIndex(): boolean {
    return this.quote.issueType === QuoteIssueType.INDEX;
  }

  /**
   * Return true if quote issue type is 'OPTION'
   *
   * @return
   */
  isOption(): boolean {
    return this.quote.issueType === QuoteIssueType.OPTION;
  }

  /**
   * Emits event to show interlisted exchange quotes
   */
  onShowInterlistedExchangeQuote(): void {
    this.onShowExchange.emit();
  }

  getSymbolStatus(): TickerSymbolStatus {
    if (this.quote !== undefined) {
      const quote = { ...this.quote };
      quote.securityHalted = this.symbolDetailSecurityHalted();
      return WatchlistUtils.toSymbolStatus(quote, this.symbol);
    }
  }

  showInterlistedExchanges(): boolean {
    return this.isStockEtf() &&
           this.hasInterlistedExchanges() &&
           !this.symbolDetailSecurityHalted() && !this.hideInterlistedTab();
  }

  showAddToWatchlist(): boolean {
    return !this.hideAddToWatchlistButton &&
           this.hasWatchListPermission();
  }

  private symbolDetailSecurityHalted(): boolean {
    return this.displaySymbolDetail && this.quote?.isHalted;
  }

  private hideInterlistedTab(): boolean {
    return this.hideInterlistExchangeForOTC &&
      (this.quote?.interlistedExchanges.find((exchange: InterlistedExchange): boolean => exchange.exchange.includes('OTC')) !== undefined ||
      this.quote?.market.includes('OTC'));
  }

  private hasWatchListPermission(): boolean {
    return this.securityService.hasPermission('WATCHLIST');
  }
}
